import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ToggleSwitch from '../../../../shared/FormStyles/ToggleSwitch.jsx';
import useAutomateForm from './useAutomateForm';
import { deleteTilesDigestAutomator } from './data';
import {
  BIWEEKLY,
  DAILY,
  WEEKLY,
  MONTHLY,
  days,
  frequencyOptions,
  timeOptions,
} from './Constants';
import {
  ButtonContainer,
  Checkbox,
  CheckboxContainer,
  CheckboxLabel,
  CheckboxWrapper,
  Container,
  Label,
  LearnMoreLink,
  ScheduledMessage,
  SelectWrapper,
  SelectLabel,
  StyledSelect,
  SwitchWrapper,
  Title,
  TitleLearnMoreWrapper,
} from './StyledComponents';
import { Button } from '../../../../shared/FormStyles/Button';

const AutomateForm = ({ automator, scheduledMessage }) => {
  const {
    handleSubmit,
    values,
    handleChange,
    setFieldValue,
  } = useAutomateForm({ automator, scheduledMessage });

  const { t } = useTranslation('send');

  const queryClient = useQueryClient();

  const { mutate: deleteAutomator } = useMutation(deleteTilesDigestAutomator, {
    onSuccess: ({ data }) => {
      queryClient.setQueryData(['updateAutomator'], data);
    },
  });

  const getFrequency = frequencyOptions.find(
    option => option.value === values.frequencyCd,
  );

  const generateDayOptions = () => {
    if (values.frequencyCd === WEEKLY || values.frequencyCd === BIWEEKLY) {
      return days.map((day, index) => ({ value: index, label: `${day}` }));
    }
    if (values.frequencyCd === MONTHLY) {
      return days.map((day, index) => ({
        value: index,
        label: `on the first  ${day}`,
      }));
    } else if (values.frequencyCd === DAILY) {
      return [];
    }
  };

  const getDayOption = () => {
    const options = generateDayOptions();
    return options.find(option => option.value === values.day);
  };

  const getTimeOption = timeOptions.find(
    option => option.value === values.time,
  );

  return (
    <form onSubmit={handleSubmit}>
      <Container>
        <TitleLearnMoreWrapper>
          <Title>{t('automate.automateTileDigest')}</Title>
          <LearnMoreLink
            href="http://learning.airbo.com/the-airbo-platform/features/automate-tile-digests"
            target="_blank"
          >
            {t('automate.learnMore')}
          </LearnMoreLink>
        </TitleLearnMoreWrapper>
        <ScheduledMessage>{values.scheduledMessage}</ScheduledMessage>
      </Container>
      <Container>
        <SelectWrapper>
          <SelectLabel>{t('automate.frequency')}</SelectLabel>
          <StyledSelect
            classNamePrefix="react-select"
            name="frequencyCd"
            options={frequencyOptions}
            value={getFrequency}
            onChange={option => setFieldValue('frequencyCd', option.value)}
            components={{
              IndicatorSeparator: () => null,
            }}
          />
        </SelectWrapper>
        {values.frequencyCd !== 0 && (
          <SelectWrapper>
            <SelectLabel>{t('automate.day')}</SelectLabel>
            <StyledSelect
              classNamePrefix="react-select"
              name="day"
              options={generateDayOptions()}
              value={getDayOption()}
              onChange={option => setFieldValue('day', option.value)}
              components={{
                IndicatorSeparator: () => null,
              }}
            />
          </SelectWrapper>
        )}
        <SelectWrapper>
          <SelectLabel>{t('automate.time')}</SelectLabel>
          <StyledSelect
            classNamePrefix="react-select"
            name="time"
            options={timeOptions}
            value={getTimeOption}
            onChange={option => setFieldValue('time', option.value)}
            components={{
              IndicatorSeparator: () => null,
            }}
          />
        </SelectWrapper>
        <SwitchWrapper>
          <Label>{t('automate.activeUsers')}</Label>
          <ToggleSwitch
            onChange={handleChange}
            value={values.activeUsers}
            name="activeUsers"
            id="activeUsers"
          />
        </SwitchWrapper>
        <CheckboxContainer>
          <Label>{t('automate.settings')}</Label>
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              name="hasFollowUp"
              id="hasFollowUp"
              checked={values.hasFollowUp}
              onChange={handleChange}
            />
            <CheckboxLabel for="hasFollowUp">
              {t('automate.sendFollowUp')}
            </CheckboxLabel>
          </CheckboxWrapper>
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              name="includeSms"
              id="includeSms"
              checked={values.includeSms}
              onChange={handleChange}
            />
            <CheckboxLabel for="includeSms">
              {t('automate.includeText')}
            </CheckboxLabel>
          </CheckboxWrapper>
        </CheckboxContainer>
        <ButtonContainer>
          <Button
            type="button"
            variant="containedRed"
            disabled={!values.persisted}
            onClick={() => deleteAutomator(values)}
          >
            {t('automate.removeAutomation')}
          </Button>
          <Button
            ml="8px"
            variant="containedBlue"
            type="submit"
            width="195px"
            padding="11px 0px"
          >
            {values.persisted
              ? t('automate.updateAutomator')
              : t('automate.scheduleTileDigest')}
          </Button>
        </ButtonContainer>
      </Container>
    </form>
  );
};

export default AutomateForm;
